import gql from 'graphql-tag';

import { IUser } from '../components/user_context_provider';
import {
  CommonsOffersCreateOfferForm,
  CommonsOffersUpdateOfferForm,
  CommonsOffersEditOfferForm,
  CommonsOffersNewOfferForm,
  CoreAutoOffersCreateAutoOfferMutation,
  CoreAutoOffersDeleteAutoOfferMutation,
} from '../rql';
import { withGraphql } from '../with_graphql';

const SellerOfferListingFragment = gql`
  fragment SellerOfferListingFragment on Listing {
    _id
    sellerPrice {
      amount
      amountCents
      currency
      symbol
      display
    }
    sellerCost {
      display
    }
  }
`;

const OfferListingFragment = gql`
  fragment OfferListingFragment on Listing {
    _id
    id
    title
    hasInventory
    inventory
    images(input: {transform: "card_square" }) {
      source
    }
    condition {
      displayName
      conditionSlug
    }
    price {
      amount
      amountCents
      currency
      symbol
      display
    }
    taxIncluded
    taxIncludedHint
    shop {
      _id
      name
      currency
      slug
      address {
        displayLocation
        locality
        region
        postalCode
        countryCode
      }
    }
    sale {
      id
    }
  }
`;

const editOfferFormQuery = gql`query Commons_Offers_EditOfferForm(
  $listingId: String,
  $negotiationId: String,
  $isSeller: Boolean!,
) {
  exchangeRates(input: { inverseReverseRates: true }) {
    rates {
      from
      to
      rate
    }
  }
  listing(input: { id: $listingId }) {
    _id
    ...OfferListingFragment
    ...SellerOfferListingFragment @include(if: $isSeller)
  }
  me {
    uuid
    activeNegotiation: myNegotiations(input: { ids: [$negotiationId] }) {
      negotiations {
        _id
        id
        buyerUuid
        sellerUuid
        taxIncluded
        taxIncludedHint
        sellerShippingWarning
        buyer {
          _id
          name
          shop {
            name
            slug
          }
        }
        shippingLocation {
          displayLocation
        }
        lastOffer {
          status
          offerItems {
            quantity
          }
          prices {
            price {
              display {
                amount
                amountCents
                currency
                symbol
                display
              }
              original {
                amount
                amountCents
                currency
                symbol
                display
              }
            }
            shippingPrice {
              display {
                amount
                amountCents
                currency
                symbol
                display
              }
              original {
                amount
                amountCents
                currency
                symbol
                display
              }
            }
            totalPrice {
              display {
                amount
                amountCents
                currency
                symbol
                display
              }
              original {
                amount
                amountCents
                currency
                symbol
                display
              }
            }
          }
        }
      }
    }
  }
}
${OfferListingFragment}
${SellerOfferListingFragment}
`;

// shippingRegionCode gets set instead of usePrimaryShippingAddress if the user changes shipping info
const newOfferFormQuery = gql`query Commons_Offers_NewOfferForm(
  $listingId: String,
  $shippingRegionCode: String,
  $usePrimaryShippingAddress: Boolean,
  $isSeller: Boolean!,
) {
  countries {
    countries {
      name
      countryCode
    }
  }
  exchangeRates(input: { inverseReverseRates: true }) {
    rates {
      from
      to
      rate
    }
  }
  listing(input: { id: $listingId }) {
    _id
    ...OfferListingFragment
    ...SellerOfferListingFragment @include(if: $isSeller)
    shipping(input: {
      shippingRegionCode: $shippingRegionCode
      usePrimaryShippingAddress: $usePrimaryShippingAddress,
    }) {
      localPickupOnly
      shippingRegionCode
      shippingAddress {
        postalCode
      }
      shippingPrices {
        rate {
          amount
          amountCents
          currency
          symbol
          display
        }
        originalRate {
          amount
          amountCents
          currency
          symbol
          display
        }
        shippingMethod
        carrierCalculated
        destinationPostalCodeNeeded
      }
    }
    priceRecommendation {
      priceMiddle {
        amountCents
        currency
      }
    }
  }
  me {
    uuid
    offerCount: myNegotiations(
      input: { state: ACCEPTED, as: BUYER, limit: 1 }
    ) {
      total
    }
  }
}
${OfferListingFragment}
${SellerOfferListingFragment}
`;

interface QueryProps {
  isDisplayed: boolean;
  listingId: string;
  isSeller: boolean;
  user: IUser;
}

export const connectCreateOfferFormQuery = withGraphql<
QueryProps,
CommonsOffersNewOfferForm.Query,
CommonsOffersNewOfferForm.Variables
>(
  newOfferFormQuery,
  {
    skip: props => !props.isDisplayed,
    options: props => {
      return {
        ssr: false,
        errorPolicy: 'all',
        variables: {
          usePrimaryShippingAddress: true,
          listingId: String(props.listingId),
          isSeller: !!props.isSeller,
        },
      };
    },
  },
);

interface UpdateFormProps {
  listingId: string;
  isSeller: boolean;
  negotiationId: string;
  isDialogOpen: boolean;
}

export const connectEditOfferFormQuery = withGraphql<
UpdateFormProps,
CommonsOffersEditOfferForm.Query,
CommonsOffersEditOfferForm.Variables
>(
  editOfferFormQuery,
  {
    skip: (props: { isDialogOpen: boolean }) => !props.isDialogOpen,
    options: props => ({
      ssr: false,
      variables: {
        listingId: String(props.listingId),
        negotiationId: String(props.negotiationId),
        isSeller: !!props.isSeller,
      },
    }),
  },
);

export const connectCreateInitialOfferMutation = withGraphql<{}, CommonsOffersCreateOfferForm.Mutation, CommonsOffersCreateOfferForm.Variables>(
  gql`mutation Commons_Offers_CreateOfferForm($input: Input_core_apimessages_CreateNegotiationRequest) {
    createNegotiation(input: $input) {
      negotiations {
        id
        lastOffer {
          offerItems {
            listingId
            quantity
          }
          initiatingParty
          prices {
            price {
              original {
                amount
                amountCents
                currency
                symbol
                display
              }
            }
            shippingPrice {
              original {
                amount
                amountCents
                currency
                symbol
                display
              }
            }
            totalPrice {
              display {
                amount
                amountCents
                currency
                symbol
                display
              }
              original {
                amount
                amountCents
                currency
                symbol
                display
              }
            }
          }
        }
      }
    }
  }`,
  {
    name: 'createInitialOffer',
    options: {
      ssr: false,
    },
  });

export const connectUpdateNegotiationMutation = withGraphql<{}, CommonsOffersUpdateOfferForm.Mutation, CommonsOffersUpdateOfferForm.Variables>(
  gql`mutation Commons_Offers_UpdateOfferForm($input: Input_core_apimessages_UpdateNegotiationRequest){
    updateNegotiation(input: $input) {
      negotiation {
        id
        state
      }
    }
  }`,
  {
    name: 'updateNegotiation',
    options: {
      ssr: false,
    },
  });

// Auto Offers
export const AutoOfferListingFragment = gql`
  fragment AutoOfferListingFragment on Listing {
    _id
    id
    title
    price {
      amount
      amountCents
      currency
      symbol
      display
    }
    condition {
      displayName
    }
    sellerCost {
      display
    }
    taxIncluded
    taxIncludedHint
    sellerPrice {
      amountCents
      display
      currency
    }
    counts {
      watchers
    }
    autoOffers {
      id
      listingId
      message
      price {
        amount
        currency
      }
      percentage
    }
  }
`;

export const createAutoOfferMutation = withGraphql<
{},
CoreAutoOffersCreateAutoOfferMutation.Mutation,
CoreAutoOffersCreateAutoOfferMutation.Variables
>(
  gql`
    mutation Core_AutoOffers_CreateAutoOfferMutation(
      $input: Input_core_apimessages_CreateAutoOfferRequest
    ) {
      createAutoOffer(input: $input) {
        autoOffer {
          id
          listingId
          message
          price {
            amount
            amountCents
            currency
            symbol
            display
          }
          percentage
        }
      }
    }
  `,
  {
    name: 'createAutoOffer',
  },
);

export const deleteAutoOfferMutation = withGraphql<
{},
CoreAutoOffersDeleteAutoOfferMutation.Mutation,
CoreAutoOffersDeleteAutoOfferMutation.Variables
>(
  gql`
    mutation Core_AutoOffers_DeleteAutoOfferMutation(
      $input: Input_core_apimessages_DeleteAutoOfferRequest
    ) {
      deleteAutoOffer(input: $input) {
        autoOffer {
          id
          listingId
          message
          price {
            amount
            amountCents
            currency
            symbol
            display
          }
          percentage
        }
      }
    }
  `,
  {
    name: 'deleteAutoOffer',
  },
);
